import type { ChangeEvent, FC } from 'react';
import { useEffect, useRef } from 'react';
import type { InputRef } from 'antd';

import { observer } from 'mobx-react';

import { Input } from '@/components/Common/UIKit';
import { useStores } from '@/components/hooks';
import { PolicySettingsGroup } from '@/types/types';
import i18n from '@/content';

import PolicySection from './PolicySection';
import CollapsiblePolicyCard from './CollapsiblePolicyCard';
import styles from './PolicySettings.module.scss';

const nameSpace = 'policyEditor';

interface TopLevelSection {
    key: string;
    titleKey: string;
    groups: PolicySettingsGroup[];
}

const TOP_LEVEL_SECTIONS: TopLevelSection[] = [
    {
        key: 'policyConfiguration',
        titleKey: 'policyEditor.sections.policyConfiguration',
        groups: ['access'],
    },
    {
        key: 'accessControl',
        titleKey: 'policyEditor.sections.accessControl',
        groups: ['access'],
    },
    {
        key: 'dataProtection',
        titleKey: 'policyEditor.sections.dataProtection',
        groups: ['access'],
    },
    {
        key: 'otherSettings',
        titleKey: 'policyEditor.sections.otherSettings',
        groups: ['access'],
    },
];

const PolicySettings: FC = observer(() => {
    const {
        policyStore: {
            changedPolicy,
            setEditingPolicyName,
        },
    } = useStores();

    const containerRef = useRef<HTMLDivElement>(null);
    const nameInputRef = useRef<InputRef>(null);

    const changeName = (event: ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        setEditingPolicyName(value);
    };

    /**
     * Scroll to top and focus input when creating a new policy
     */
    useEffect(() => {
        if (changedPolicy?.id === '') {
            // Scroll the policy-settings container (which has overflow-y: auto) to top
            if (containerRef.current) {
                containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
            // Focus the policy name input
            setTimeout(() => {
                nameInputRef.current?.focus();
            }, 100);
        }
    }, [changedPolicy?.id]);

    if (!changedPolicy) {
        return null;
    }

    const { settings } = changedPolicy;

    return (
        <div className={styles['policy-settings']} ref={containerRef}>
            {changedPolicy.id === '' && (
                <div className={styles['new-policy-input-wrapper']}>
                    <span className={styles['section-title']}>
                        {i18n.t(`${nameSpace}.policyName`)}
                    </span>
                    <Input
                        ref={nameInputRef}
                        onChange={changeName}
                        className={styles['name-input']}
                        value={changedPolicy.title}
                    />
                </div>
            )}

            <div className={styles['policy-cards-container']}>
                {TOP_LEVEL_SECTIONS.map((section) => {
                    const hasSettings = section.groups.some((group) => settings[group]?.length > 0);

                    if (!hasSettings) {
                        return null;
                    }

                    return (
                        <CollapsiblePolicyCard
                            key={section.key}
                            title={i18n.t(section.titleKey)}
                            defaultCollapsed={false}
                        >
                            {section.groups.map((group) => {
                                const groupSettings = settings[group];
                                if (!groupSettings || groupSettings.length === 0) {
                                    return null;
                                }

                                return (
                                    <PolicySection
                                        key={group}
                                        settingsGroupType={group}
                                        policySettings={groupSettings}
                                        filterBySection={section.key}
                                    />
                                );
                            })}
                        </CollapsiblePolicyCard>
                    );
                })}
            </div>
        </div>
    );
});

export default PolicySettings;
